import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  DeliveryItem,
  DeliveryStatusItem,
  NextStatusItem,
  NextTicketTag,
  OrderCustomerItem,
  OrderItem,
  OrderStatusItem,
  TicketItem,
} from 'src/app/api/models';
import {
  DeliveryService,
  OrderService,
  TicketService,
  TicketStatusService,
} from 'src/app/api/services';

@Component({
  selector: 'app-modal-status-change',
  templateUrl: './modal-status-change.component.html',
  styleUrls: ['./modal-status-change.component.scss'],
})
export class ModalStatusChangeComponent {
  status?: OrderStatusItem;
  order?: OrderItem;
  customer_order?: OrderCustomerItem;
  delivery?: DeliveryItem;
  ticket?: TicketItem;
  statuses: NextStatusItem[] = [];

  type!: 'order' | 'customer_order' | 'ticket' | 'delivery';
  // delivery_statuses: NextStatusItem[] = [];
  // ticket_statuses: NextStatusItem[] = [];

  tags: NextTicketTag[] = [];

  // Nota integrazione richiesta dalla transizione scelta (evento del workflow): null se non c'è
  integrationNote: { required: boolean } | null = null;
  readonly maxIntegrationNoteLength = 4000;

  // Soluzione indicata passando in Chiuso: obbligatoria se la commessa non consente la chiusura senza soluzione
  solutionRequired = false;
  solutionLoading = false;

  changeStatusForm = new FormGroup({
    next_status: new FormControl<NextStatusItem | undefined>(undefined, Validators.required),
    next_tag: new FormControl<number | undefined>(undefined),
    closed_on: new FormControl<Date | undefined>(undefined),
    integration_note: new FormControl<string>('', { nonNullable: true }),
    solution: new FormControl<string>('', { nonNullable: true }),
  });

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public dialogService: DialogService,
    private orderStatusService: OrderService,
    private deliveryService: DeliveryService,
    private ticketStatusService: TicketStatusService,
    private ticketService: TicketService
  ) {
    this.status = config.data.status;
    this.order = config.data.order;
    this.customer_order = config.data.customer_order;
    this.delivery = config.data.delivery;
    this.ticket = config.data.ticket;
  }

  ngOnInit() {
    this.getAllStatuses();

    this.changeStatusForm.get('next_status')?.valueChanges.subscribe(value => {
      if (this.ticket) {
        if (value?.to === 3) {
          this.changeStatusForm.get('closed_on')?.setValidators(Validators.required);
          this.loadSolutionForClose();
        } else {
          this.changeStatusForm.get('closed_on')?.clearValidators();
          this.setSolutionRule(false);
        }
        this.tags = value?.ticket_tags || [];

        // evento "nota integrazione" della transizione: obbligatoria o facoltativa
        const noteEvent = ((value?.events ?? []) as any[]).find(
          e => e?.type === 'integration_note'
        );
        this.integrationNote = noteEvent ? { required: !!noteEvent.config?.required } : null;
        const noteCtrl = this.changeStatusForm.get('integration_note')!;
        noteCtrl.setValue('');
        noteCtrl.setValidators(
          this.integrationNote
            ? [
                Validators.maxLength(this.maxIntegrationNoteLength),
                ...(this.integrationNote.required
                  ? [(c: any) => ((c.value ?? '').trim() ? null : { required: true })]
                  : []),
              ]
            : []
        );
        noteCtrl.updateValueAndValidity();
      }
      this.changeStatusForm.get('closed_on')?.updateValueAndValidity();
    });
  }

  // Alla chiusura il BE dice se la soluzione è obbligatoria (tipo ticket e flag della commessa) e qual è quella attuale
  private loadSolutionForClose() {
    if (!this.ticket) return;
    this.solutionLoading = true;
    this.ticketService
      .ticketControllerCloseCheck({ body: { ids: [this.ticket.id], include_solution: true } })
      .subscribe({
        next: res => {
          const info = res.data?.list?.[0];
          this.solutionLoading = false;
          this.changeStatusForm.get('solution')?.setValue(info?.solution_description ?? '');
          this.setSolutionRule(!!info?.requires_solution);
        },
        error: () => {
          // senza la verifica il campo resta facoltativo, precompilato con la soluzione del ticket; il BE controlla comunque
          this.solutionLoading = false;
          this.changeStatusForm.get('solution')?.setValue(this.ticket?.solution_description ?? '');
          this.setSolutionRule(false);
        },
      });
  }

  private setSolutionRule(required: boolean) {
    this.solutionRequired = required;
    const ctrl = this.changeStatusForm.get('solution')!;
    ctrl.setValidators(
      required ? [(c: any) => ((c.value ?? '').trim() ? null : { required: true })] : []
    );
    ctrl.updateValueAndValidity();
  }

  get closingTicket(): boolean {
    return !!this.ticket && this.changeStatusForm.value.next_status?.to === 3;
  }

  getAllStatuses() {
    // se si tratta di ordini fornitore
    if (this.order) {
      this.type = 'order';
      if (this.order!.related_status_workflow_id != null) {
        this.orderStatusService.orderControllerFindOrderNodes({ id: this.order!.id }).subscribe({
          next: res_nodes => {
            // filtro gli status in base ai nodi del workflow
            this.statuses = res_nodes.data.list;
          },
        });
      } else {
        this.orderStatusService.orderStatusControllerGetAll().subscribe({
          next: res => {
            // prendo tutti gli status
            this.statuses = res.data.list.map(
              s =>
                ({
                  to: s.id,
                  from: this.order!.status_id,
                  to_label: s.label,
                  from_label: res.data.list.find(el => el.id === this.order?.status_id)?.label,
                  events: [],
                }) as NextStatusItem
            );
          },
        });
      }
    }
    // se si tratta di ordini cliente
    if (this.customer_order) {
      this.type = 'customer_order';
      if (this.customer_order?.related_status_workflow != null) {
        this.orderStatusService
          .orderCustomerControllerFindOrderCustomerNodes({ id: this.customer_order!.id })
          .subscribe({
            next: res_nodes => {
              // filtro gli status in base ai nodi del workflow
              this.statuses = res_nodes.data.list;
            },
          });
      } else {
        this.orderStatusService
          .customerOrderStatusControllerFindAll({ limit: 10000, page: 1 })
          .subscribe({
            next: res => {
              // prendo tutti gli status
              this.statuses = res.data.items.map(
                s =>
                  ({
                    to: s.id,
                    from: this.customer_order!.related_status?.id,
                    to_label: s.label,
                    from_label: res.data.items.find(
                      el => el.id === this.customer_order?.related_status.id
                    )?.label,
                    events: [],
                  }) as NextStatusItem
              );
            },
          });
      }
    }
    // se si tratta di una delivery
    if (this.delivery) {
      this.type = 'delivery';
      if (this.delivery!.status_workflow?.id != null) {
        this.deliveryService
          .deliveryControllerFindDeliveryStatusNodes({ id: +this.delivery!.id })
          .subscribe({
            next: res_nodes => {
              // filtro gli status in base ai nodi del workflow
              // this.delivery_statuses = res_nodes.data.list;
              this.statuses = res_nodes.data.list;
            },
          });
        return;
      } else {
        this.deliveryService.deliveryStatusControllerFindAll({ limit: 10000, skip: 0 }).subscribe({
          next: res => {
            // prendo tutti gli status
            this.statuses = res.data.list.map(
              s =>
                ({
                  to: s.id,
                  from: this.delivery!.delivery_status.id,
                  to_label: s.description,
                  from_label: this.delivery!.delivery_status.description,
                  events: [],
                }) as NextStatusItem
            );
          },
        });
      }
    }

    // se si tratta di un ticket
    if (this.ticket) {
      this.type = 'ticket';
      const agreement = this.ticket.related_maintenance_agreement || this.ticket.related_agreement;
      const workflow =
        this.ticket.related_maintenance_agreement?.ticket_workflow ??
        this.ticket.related_agreement?.ticket_workflow;

      if (workflow?.id != null) {
        this.ticketService.ticketControllerFindTicketNodes({ id: +this.ticket!.id }).subscribe({
          next: res_nodes => {
            // filtro gli status in base ai nodi del workflow
            // this.ticket_statuses = res_nodes.data.list;
            this.statuses = res_nodes.data.list;
          },
        });
        return;
      } else {
        this.ticketStatusService
          .ticketStatusControllerFindAll({
            skip: 0,
            limit: 0,
            visible: true,
            ticket_type_id: this.ticket.ticket_type?.id,
          })
          .subscribe({
            next: res => {
              // prendo tutti gli status
              this.statuses = res.data.list.map(
                s =>
                  ({
                    to: s.id,
                    from: agreement?.ticket_workflow?.id,
                    to_label: s.description,
                    from_label: agreement?.ticket_workflow?.name,
                    events: [],
                    ticket_tags: s.ticket_tags.map(
                      tag => ({ id: tag.id, description: tag.description }) as NextTicketTag
                    ),
                  }) as NextStatusItem
              );
              console.log('Statuses: ', this.statuses);
            },
          });
      }
    }
  }

  close() {
    this.ref.close();
  }

  save() {
    this.ref.close({
      value: this.changeStatusForm.value.next_status,
      ticket_tag: this.changeStatusForm.value.next_tag,
      closed_on: this.changeStatusForm.value.closed_on,
      integration_note: this.integrationNote
        ? this.changeStatusForm.value.integration_note?.trim() || undefined
        : undefined,
      // vuota = il ticket non viene toccato
      solution_description: this.closingTicket
        ? this.changeStatusForm.value.solution?.trim() || undefined
        : undefined,
    });
  }
}
